import axios from 'axios';
import { getAuthHeader } from './auth';
import { API_BASE_URL } from '../config/api';

export interface Role {
  id: string;
  name: string;
  displayName: string;
  description: string;
  permissions: string[];
  level: number;
  isSystem: boolean;
  isActive: boolean;
  organizationId?: string;
  color?: string;
  icon?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface UserRole {
  id: string;
  userId: string;
  roleId: string;
  organizationId: string;
  assignedBy: string;
  assignedAt: string;
  expiresAt?: string;
  isActive: boolean;
  permissionOverrides?: {
    granted?: string[];
    revoked?: string[];
  };
  revokedBy?: string;
  revokedAt?: string;
  revokeReason?: string;
  role?: Role; // Populated role data
}

export interface UserWithRoles {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  profile?: {
    title?: string;
    department?: string;
    licenseNumber?: string;
    specialties?: string[];
    phoneNumber?: string;
  };
  /** The user's role (users.role): what their access is built from. */
  role?: string;
  userRoles: UserRole[];
  roles: Role[];
  primaryRole: Role;
  allPermissions: string[];
}


export interface Invitation {
  id: string;
  email: string;
  role: string;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
  invitedBy: string;
  expiresAt: string;
  createdAt: string;
  emailSent?: boolean;
  emailSentAt?: string;
  emailError?: string;
  metadata?: {
    firstName?: string;
    lastName?: string;
    department?: string;
  };
}

export interface InviteUserData {
  email: string;
  role: string;
  metadata?: {
    firstName?: string;
    lastName?: string;
    department?: string;
  };
}

class RoleService {
  private getHeaders(): Record<string, string> {
    const authHeader = getAuthHeader();
    return {
      'Content-Type': 'application/json',
      ...(authHeader.Authorization ? { Authorization: authHeader.Authorization } : {})
    };
  }

  // Get all available roles
  async getRoles(includeSystem: boolean = true): Promise<Role[]> {
    try {
      const params = new URLSearchParams();
      if (!includeSystem) {
        params.append('excludeSystem', 'true');
      }

      const response = await axios.get(
        `${API_BASE_URL}/v1/auth/roles?${params.toString()}`,
        { headers: this.getHeaders() }
      );

      return response.data.data || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch roles');
    }
  }


  // Get users with their roles
  async getUsersWithRoles(filters: {
    page?: number;
    limit?: number;
    search?: string;
    roleId?: string;
    isActive?: boolean;
  } = {}): Promise<{
    users: UserWithRoles[];
    total: number;
    page: number;
    limit: number;
  }> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const response = await axios.get(
        `${API_BASE_URL}/v1/auth/users-with-roles?${params.toString()}`,
        { headers: this.getHeaders() }
      );

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch users with roles');
    }
  }


  // Batch update user roles
  async updateUserRoles(userId: string, roleIds: string[]): Promise<{
    userId: string;
    userRoles: UserRole[];
    rolesAdded: number;
    rolesRemoved: number;
  }> {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/v1/auth/users/${userId}/roles`,
        { roleIds },
        { headers: this.getHeaders() }
      );

      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update user roles');
    }
  }


  // Invite a new user
  async inviteUser(userData: InviteUserData): Promise<Invitation> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/v1/auth/invite-user`,
        userData,
        { headers: this.getHeaders() }
      );

      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to send invitation');
    }
  }

  // Get organization invitations
  async getInvitations(status?: string): Promise<Invitation[]> {
    try {
      const params = new URLSearchParams();
      if (status) {
        params.append('status', status);
      }

      const response = await axios.get(
        `${API_BASE_URL}/v1/auth/invitations?${params.toString()}`,
        { headers: this.getHeaders() }
      );

      return response.data.data || [];
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch invitations');
    }
  }

  // Resend invitation
  async resendInvitation(invitationId: string): Promise<Invitation> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/v1/auth/invitations/${invitationId}/resend`,
        {},
        { headers: this.getHeaders() }
      );

      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to resend invitation');
    }
  }

  // Cancel invitation
  async cancelInvitation(invitationId: string): Promise<void> {
    try {
      await axios.post(
        `${API_BASE_URL}/v1/auth/invitations/${invitationId}/cancel`,
        {},
        { headers: this.getHeaders() }
      );
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to cancel invitation');
    }
  }

  // Update user details
  async updateUser(userId: string, data: {
    email?: string;
    firstName?: string;
    lastName?: string;
    role?: string;
    isActive?: boolean;
    profile?: {
      title?: string;
      department?: string;
      licenseNumber?: string;
      specialties?: string[];
      phoneNumber?: string;
    };
  }): Promise<any> {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/v1/auth/users/${userId}`,
        data,
        { headers: this.getHeaders() }
      );

      return response.data.data || response.data.user;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update user');
    }
  }

  // Send password reset email to user (admin action)
  async sendPasswordReset(userId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/v1/auth/users/${userId}/send-password-reset`,
        {},
        { headers: this.getHeaders() }
      );

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to send password reset email');
    }
  }

  // Deactivate user
  async deactivateUser(userId: string): Promise<any> {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/v1/auth/users/${userId}/deactivate`,
        {},
        { headers: this.getHeaders() }
      );

      return response.data.user;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to deactivate user');
    }
  }

  // Activate user
  async activateUser(userId: string): Promise<any> {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/v1/auth/users/${userId}/activate`,
        {},
        { headers: this.getHeaders() }
      );

      return response.data.user;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to activate user');
    }
  }
}

export const roleService = new RoleService();